// src/hooks/useScanner.js
//
// Обёртка над @capacitor-mlkit/barcode-scanning.
// Открывает камеру, ждёт сканирования одного баркода, возвращает Promise.
//
// На вебе/десктопе — заглушка, возвращает null и показывает alert
// (можно потом добавить ZXing для веба, но MVP пока без этого).
//
// Использование:
//   const { scan, supported } = useScanner();
//   const code = await scan();
//   if (code) { ... parseBarcode(code) ... }

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

let _mlKitModule = null;
async function getMLKit() {
  if (_mlKitModule) return _mlKitModule;
  try {
    _mlKitModule = await import('@capacitor-mlkit/barcode-scanning');
    return _mlKitModule;
  } catch (e) {
    console.warn('[scanner] @capacitor-mlkit/barcode-scanning не установлен', e);
    return null;
  }
}

async function isNative() {
  try {
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// Готовность камеры-сканера кэшируется на сессию: проверка модуля через
// Google Play Services и запрос разрешения — медленные, и раньше они гонялись
// НА КАЖДЫЙ тап. Теперь один раз убедились — дальше открываем камеру мгновенно.
let _permGranted = false;
let _moduleReady = false;

async function ensureScannerReady(BarcodeScanner) {
  // разрешение камеры
  if (!_permGranted) {
    const perm = await BarcodeScanner.checkPermissions();
    if (perm.camera !== 'granted') {
      const req = await BarcodeScanner.requestPermissions();
      if (req.camera !== 'granted') return false;
    }
    _permGranted = true;
  }
  // модуль ML Kit (на части устройств его надо доустановить)
  if (!_moduleReady) {
    try {
      const isAvail = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
      if (!isAvail.available) {
        await BarcodeScanner.installGoogleBarcodeScannerModule();
      }
      _moduleReady = true;
    } catch {
      // не критично — не блокируем скан, просто не помечаем готовым
    }
  }
  return true;
}

// Прогрев при старте приложения: проверить разрешение и модуль заранее,
// чтобы даже первый тап по кнопке открывал камеру без задержки.
export async function warmUpScanner() {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) return;
    const mod = await getMLKit();
    if (!mod) return;
    await ensureScannerReady(mod.BarcodeScanner);
  } catch { /* тихо */ }
}

export function useScanner() {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const scan = useCallback(async () => {
    if (busy) return null;
    setBusy(true);
    try {
      const native = await isNative();
      if (!native) {
        // Веб-версия (Safari на iPhone/iPad, браузер на ПК): камера через
        // html5-qrcode. Беспроводной HID-сканер работает отдельно и здесь
        // не участвует — он слушает клавиатуру.
        const { scanWithCamera } = await import('../utils/webScanner');
        return await scanWithCamera({
          title: t('scanner.webTitle', 'Наведите на штрихкод'),
          cancel: t('common.cancel', 'Отмена'),
          hint: t('scanner.webHint', 'Держите код в кадре целиком'),
          denied: t('scanner.webDenied', 'Нет доступа к камере. Разрешите доступ в настройках браузера и попробуйте снова.'),
          noCamera: t('scanner.webNoCamera', 'Камера не найдена. Введите код вручную.'),
        });
      }

      const mod = await getMLKit();
      if (!mod) {
        alert(t('scanner.moduleNotInstalled',
          'Модуль сканера не установлен. Обратитесь к разработчику.'));
        return null;
      }
      const { BarcodeScanner } = mod;

      // Проверка разрешения и модуля — один раз за сессию (кэшируется).
      const ready = await ensureScannerReady(BarcodeScanner);
      if (!ready) {
        alert(t('scanner.noPermission', 'Нет разрешения на использование камеры'));
        return null;
      }

      // Запускаем сканирование
      const result = await BarcodeScanner.scan({
        formats: [
          // Линейные баркоды
          'EAN_8', 'EAN_13', 'UPC_A', 'UPC_E',
          'CODE_39', 'CODE_93', 'CODE_128',
          'ITF',
          // 2D коды (включая Data Matrix для маркировки)
          'QR_CODE', 'DATA_MATRIX', 'PDF_417', 'AZTEC',
        ],
      });

      if (result?.barcodes && result.barcodes.length > 0) {
        // Берём первый отсканированный
        return result.barcodes[0].rawValue || result.barcodes[0].displayValue || null;
      }
      return null;
    } catch (e) {
      console.error('[scanner] error:', e);
      if (e?.message && !/cancel/i.test(e.message)) {
        alert(t('scanner.error', 'Ошибка сканирования: ') + e.message);
      }
      return null;
    } finally {
      setBusy(false);
    }
  }, [busy, t]);

  return { scan, busy };
}
